import ExcelJS, { type CellValue } from "exceljs";

/** Một dòng đọc từ phiếu đếm — gửi thẳng cho `/stock-moves/counts/import`. */
export interface OpeningSheetRow {
  row: number;
  code: string;
  subRef: string;
  warehouse: string;
  location: string;
  counted: string;
  /** Ô chữ kiểu "1.440": không rõ nghìn hay thập phân — máy chủ báo dòng lỗi thay vì đoán. */
  ambiguous?: boolean;
}

type Column = "code" | "subRef" | "warehouse" | "location" | "oldLocation" | "counted";

/** Tiêu đề cột được nhận (đã bỏ dấu, viết thường) — tiếng Việt của phiếu đếm, kèm tiếng Anh cho tệp tự làm. */
const HEADERS: Record<string, Column> = {
  "ma": "code", "code": "code", "product code": "code", "ma san pham": "code",
  "ma phu": "subRef", "sub ref": "subRef", "subref": "subRef", "sub-ref": "subRef",
  "kho thuc te": "warehouse", "kho": "warehouse", "warehouse": "warehouse",
  "vi tri thuc te": "location", "vi tri": "location", "location": "location",
  "vi tri ghi o he cu": "oldLocation", "old location": "oldLocation",
  "so dem": "counted", "counted": "counted", "so luong dem": "counted", "counted quantity": "counted",
};

const plain = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D").toLowerCase().replace(/\s+/g, " ").trim();

function text(value: CellValue): string {
  if (value == null) return "";
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)));
  if (typeof value === "string") return value.trim();
  if (typeof value === "boolean") return String(value);
  // ExcelJS trả ô ngày như giờ đồng hồ trong tệp, gắn nhãn UTC — đọc lại bằng UTC là đúng ngày ghi trong Excel (không đổi sang giờ Việt Nam).
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "object") {
    if ("richText" in value) return value.richText.map((r) => r.text).join("").trim();
    if ("result" in value) return text((value.result ?? null) as CellValue);
    if ("text" in value) return String(value.text).trim();
  }
  return String(value).trim();
}

/** Ô chữ (người gõ vào ô định dạng Text, hoặc dán từ nơi khác) — khác ô số mà Excel đã tự hiểu theo cài đặt vùng. */
function isText(value: CellValue): boolean {
  if (typeof value === "string") return true;
  if (value && typeof value === "object") {
    if ("richText" in value) return true;
    if ("result" in value) return isText((value.result ?? null) as CellValue);
    if ("text" in value) return true;
  }
  return false;
}

/**
 * Số đếm của một ô. Ô số: lấy đúng số Excel đã hiểu (theo cài đặt vùng của máy người gõ). Ô chữ: chỉ đọc dấu phân
 * cách khi KHÔNG mơ hồ — "1.440.000" / "1,440,000" (nhiều nhóm nghìn), "1.440,5" / "1,440.5" (có cả hai dấu), "1,5"
 * (thập phân kiểu Việt), "0.500" (bắt đầu bằng 0 thì không phải nhóm nghìn). "1.440" hay "1,440" (đúng một nhóm ba chữ
 * số) có thể là 1440 hoặc 1,44 — không đoán: đánh dấu để máy chủ báo dòng lỗi, vì đoán sai là tồn lệch cả nghìn lần.
 */
export function countedOf(value: CellValue): { counted: string; ambiguous: boolean } {
  const raw = text(value);
  if (!isText(value)) return { counted: raw, ambiguous: false };
  const s = raw.replace(/\s+/g, "");
  if (/^[1-9]\d{0,2}[.,]\d{3}$/.test(s)) return { counted: s, ambiguous: true };
  if (/^\d{1,3}(\.\d{3}){2,}$/.test(s)) return { counted: s.replaceAll(".", ""), ambiguous: false };
  if (/^\d{1,3}(,\d{3}){2,}$/.test(s)) return { counted: s.replaceAll(",", ""), ambiguous: false };
  if (/^\d{1,3}(\.\d{3})+,\d+$/.test(s)) return { counted: s.replaceAll(".", "").replace(",", "."), ambiguous: false };
  if (/^\d{1,3}(,\d{3})+\.\d+$/.test(s)) return { counted: s.replaceAll(",", ""), ambiguous: false };
  if (/^\d+,\d+$/.test(s)) return { counted: s.replace(",", "."), ambiguous: false };
  return { counted: s, ambiguous: false };
}

/**
 * Đọc phiếu đếm tồn đầu kỳ (.xlsx). Lấy trang "Phiếu đếm" nếu có, không thì trang đầu tiên. Dòng tiêu đề là
 * dòng đầu tiên (trong 30 dòng đầu) có cả cột Mã và cột Số đếm. Vị trí thực tế để trống thì dùng vị trí ghi ở
 * hệ cũ. Dòng trống cả mã lẫn số đếm bị bỏ qua; số dòng giữ đúng số dòng Excel để báo lỗi cho dễ tìm.
 */
export async function parseOpeningSheet(data: ArrayBuffer): Promise<{ sheet: string; rows: OpeningSheetRow[] }> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(data);
  const ws = wb.worksheets.find((w) => plain(w.name) === "phieu dem") ?? wb.worksheets[0];
  if (!ws) throw new Error("opening_sheet_empty");
  let headerRow = 0;
  const cols = new Map<Column, number>();
  for (let r = 1; r <= Math.min(30, ws.rowCount) && !headerRow; r++) {
    const found = new Map<Column, number>();
    ws.getRow(r).eachCell((cell, c) => {
      const col = HEADERS[plain(text(cell.value))];
      if (col && !found.has(col)) found.set(col, c);
    });
    if (found.has("code") && found.has("counted")) {
      headerRow = r;
      for (const [k, v] of found) cols.set(k, v);
    }
  }
  if (!headerRow) throw new Error("opening_sheet_header");
  const cell = (r: number, col: Column): CellValue => (cols.has(col) ? ws.getRow(r).getCell(cols.get(col)!).value : null);
  const get = (r: number, col: Column) => text(cell(r, col));
  const rows: OpeningSheetRow[] = [];
  for (let r = headerRow + 1; r <= ws.rowCount; r++) {
    const code = get(r, "code"), { counted, ambiguous } = countedOf(cell(r, "counted"));
    if (!code && !counted) continue;
    rows.push({ row: r, code, subRef: get(r, "subRef"), warehouse: get(r, "warehouse"), location: get(r, "location") || get(r, "oldLocation"), counted, ...(ambiguous ? { ambiguous } : {}) });
  }
  return { sheet: ws.name, rows };
}
